import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildStoryPersons } from '../utils/buildStoryPersons';

const SLIDE_DURATION_MS = 400;

export const useStoriesBar = (persons) => {
	const [storyOpen, setStoryOpen] = useState(false);
	const [activePersonIdx, setActivePersonIdx] = useState(0);
	const [activeStoryIdx, setActiveStoryIdx] = useState(0);
	const [slideState, setSlideState] = useState(null);
	const [slideAnimating, setSlideAnimating] = useState(false);

	const slideStateRef = useRef(null);
	const slideTimeoutRef = useRef(null);

	const storyPersons = useMemo(() => buildStoryPersons(persons), [persons]);
	const personCount = storyPersons.length;
	const isSliding = slideState !== null;

	useEffect(() => {
		slideStateRef.current = slideState;
	}, [slideState]);

	const clearSlideTimeout = useCallback(() => {
		if (slideTimeoutRef.current) {
			clearTimeout(slideTimeoutRef.current);
			slideTimeoutRef.current = null;
		}
	}, []);

	const finalizeSlide = useCallback(() => {
		const state = slideStateRef.current;

		if (!state) {
			return;
		}

		clearSlideTimeout();
		slideStateRef.current = null;
		setActivePersonIdx(state.toIdx);
		setActiveStoryIdx(state.enteringStoryIdx ?? 0);
		setSlideState(null);
		setSlideAnimating(false);
	}, [clearSlideTimeout]);

	const wrapPersonIdx = useCallback(
		(idx) => {
			if (personCount === 0) {
				return 0;
			}

			return (idx + personCount) % personCount;
		},
		[personCount],
	);

	const beginSlideAnimation = useCallback(() => {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				setSlideAnimating(true);
			});
		});
	}, []);

	const startPersonSlide = useCallback(
		(direction, enteringStoryIdx = 0) => {
			if (personCount <= 1 || slideStateRef.current !== null) {
				return;
			}

			const toIdx = wrapPersonIdx(
				activePersonIdx + (direction === 1 ? 1 : -1),
			);
			const targetStories = storyPersons[toIdx].stories;
			const safeEnteringIdx = Math.min(
				enteringStoryIdx,
				Math.max(targetStories.length - 1, 0),
			);

			clearSlideTimeout();
			setSlideAnimating(false);
			setSlideState({
				direction,
				fromIdx: activePersonIdx,
				fromStoryIdx: activeStoryIdx,
				toIdx,
				enteringStoryIdx: safeEnteringIdx,
			});
			beginSlideAnimation();
			slideTimeoutRef.current = setTimeout(() => {
				finalizeSlide();
			}, SLIDE_DURATION_MS);
		},
		[
			personCount,
			activePersonIdx,
			activeStoryIdx,
			storyPersons,
			beginSlideAnimation,
			clearSlideTimeout,
			finalizeSlide,
		],
	);

	const handleOpenStory = useCallback(
		(personId) => {
			const idx = storyPersons.findIndex(
				(entry) => entry.personId === personId,
			);

			if (idx === -1) {
				return;
			}

			clearSlideTimeout();
			slideStateRef.current = null;
			setSlideState(null);
			setSlideAnimating(false);
			setActivePersonIdx(idx);
			setActiveStoryIdx(0);
			setStoryOpen(true);
		},
		[storyPersons, clearSlideTimeout],
	);

	const handleNextStory = useCallback(() => {
		if (personCount === 0 || slideStateRef.current !== null) {
			return;
		}

		const stories = storyPersons[activePersonIdx].stories;

		if (activeStoryIdx < stories.length - 1) {
			setActiveStoryIdx((idx) => idx + 1);
			return;
		}

		startPersonSlide(1);
	}, [
		personCount,
		storyPersons,
		activePersonIdx,
		activeStoryIdx,
		startPersonSlide,
	]);

	const handlePrevStory = useCallback(() => {
		if (personCount === 0 || slideStateRef.current !== null) {
			return;
		}

		if (activeStoryIdx > 0) {
			setActiveStoryIdx((idx) => idx - 1);
			return;
		}

		const prevIdx = wrapPersonIdx(activePersonIdx - 1);
		const prevStories = storyPersons[prevIdx].stories;
		startPersonSlide(-1, prevStories.length - 1);
	}, [
		personCount,
		activeStoryIdx,
		wrapPersonIdx,
		activePersonIdx,
		storyPersons,
		startPersonSlide,
	]);

	const handlePeekPrev = useCallback(() => {
		startPersonSlide(-1, 0);
	}, [startPersonSlide]);

	const handlePeekNext = useCallback(() => {
		startPersonSlide(1, 0);
	}, [startPersonSlide]);

	const handleSlideComplete = useCallback(() => {
		finalizeSlide();
	}, [finalizeSlide]);

	const handleCloseStory = useCallback(() => {
		clearSlideTimeout();
		slideStateRef.current = null;
		setStoryOpen(false);
		setSlideState(null);
		setSlideAnimating(false);
	}, [clearSlideTimeout]);

	return {
		storyOpen,
		storyPersons,
		activePersonIdx,
		activeStoryIdx,
		slideState,
		slideAnimating,
		isSliding,
		handleOpenStory,
		handleNextStory,
		handlePrevStory,
		handlePeekPrev,
		handlePeekNext,
		handleSlideComplete,
		handleCloseStory,
	};
};
